import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";
import useVoiceRecorder from "@/lib/useVoiceRecorder";
import { VOICE_REQUEST_TIMEOUT_MS } from "@/lib/constants";

export default function Chat() {
  const { t, lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const voice = useVoiceRecorder();
  const scrollRef = useRef(null);

  const onVoice = async () => {
    if (voice.recording) {
      const blob = await voice.stop();
      if (!blob) return;
      setVoiceProcessing(true);
      try {
        const fd = new FormData();
        fd.append("file", blob, "voice.webm");
        const { data } = await api.post("/ingest/audio?mode=chat", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: VOICE_REQUEST_TIMEOUT_MS,
        });
        if (data.text) setInput((cur) => (cur ? cur + " " + data.text : data.text));
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setVoiceProcessing(false);
      }
    } else {
      await voice.start();
    }
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/chat/messages");
    setMessages(data.messages);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const txt = input.trim();
    setInput("");
    setSending(true);
    setError("");
    // optimistic user bubble
    setMessages((m) => [
      ...m,
      { id: `tmp-${Date.now()}`, role: "user", content: txt, created_at: new Date().toISOString() },
    ]);
    try {
      const { data } = await api.post("/chat/send", { content: txt });
      // replace tmp with confirmed pair
      setMessages((m) => {
        const withoutTmp = m.filter((x) => !String(x.id).startsWith("tmp-"));
        return [...withoutTmp, data.user_message, data.assistant_message];
      });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      // 402 = subscription gate; surface a CTA that reuses the existing
      // Lemon Squeezy checkout flow (same endpoint as Landing.startCheckout).
      setErrorCode(err.response?.status === 402 ? "subscription_required" : null);
    } finally {
      setSending(false);
    }
  };

  const [errorCode, setErrorCode] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const startCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/payments/lemonsqueezy/checkout", {
        plan: "basic",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setCheckoutLoading(false);
    }
  };

  const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString(lang === "pt" ? "pt-PT" : "en-GB", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="flex flex-col h-dvh md:h-screen">
      <header className="px-6 md:px-12 lg:px-16 py-6 md:py-8 border-b border-[var(--border)]">
        <p className="label-cap">— {t("app.sidebar.chat")}</p>
        <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-2" data-testid="chat-title">
          {t("app.chat.title")}
        </h1>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 md:px-12 lg:px-16 py-10 space-y-5" data-testid="chat-scroll">
        {messages.length === 0 && (
          <p className="font-display-italic text-[var(--ink-soft)] text-center mt-12" data-testid="chat-empty">
            {t("app.chat.empty")}
          </p>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className="max-w-[80%]">
              <div className={`bubble ${m.role === "user" ? "bubble-user" : "bubble-assistant"}`} data-testid={`chat-msg-${m.role}`}>
                {m.content}
              </div>
              <p className={`text-[11px] mt-2 tracking-monocle uppercase text-[var(--ink-faint)] ${m.role === "user" ? "text-right" : "text-left"}`}>
                {m.role === "user" ? (lang === "pt" ? "Você" : "You") : "Mordomo"} · {fmtTime(m.created_at)}
              </p>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="chat-thinking">
            <div className="bubble bubble-assistant font-display-italic text-[var(--ink-soft)]">
              {t("app.chat.thinking")}
            </div>
          </div>
        )}
        {error && (
          <div className="text-center" data-testid="chat-error">
            <p className="text-[var(--error)] text-sm">{error}</p>
            {errorCode === "subscription_required" && (
              <button
                type="button"
                onClick={startCheckout}
                disabled={checkoutLoading}
                data-testid="chat-error-subscribe-cta"
                className="mt-3 inline-block px-5 py-2 text-[11px] tracking-monocle uppercase border border-[var(--ink)] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors disabled:opacity-50"
              >
                {checkoutLoading ? t("app.chat.errorCta.loading") : t("app.chat.errorCta.subscribe")}
              </button>
            )}
          </div>
        )}
      </div>

      <form onSubmit={send} className="border-t border-[var(--border)] px-6 md:px-12 lg:px-16 py-5 pb-safe md:pb-5 bg-[var(--surface)]" data-testid="chat-form">
        <div className="flex items-end gap-3 md:pr-44">
          <button
            type="button"
            onClick={onVoice}
            disabled={voiceProcessing || sending}
            aria-label={t("app.voice")}
            className={`tap-target px-4 py-3 inline-flex items-center justify-center border ${
              voice.recording
                ? "bg-[var(--oxblood)] text-[var(--bg)] border-[var(--oxblood)] animate-pulse"
                : "border-[var(--border)] text-[var(--ink-soft)] hover:border-[var(--ink)]"
            }`}
            data-testid="chat-voice-btn"
            title={t("app.voice")}
          >
            {(() => {
              if (voice.recording) return <Square size={16} strokeWidth={2} />;
              if (voiceProcessing) return <Loader2 size={16} className="animate-spin" />;
              return <Mic size={16} strokeWidth={1.75} />;
            })()}
          </button>
          <textarea
            rows={1}
            className="line-input flex-1 resize-none"
            placeholder={t("app.chat.placeholder")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            data-testid="chat-input"
          />
          <button type="submit" disabled={sending || !input.trim()} className="btn-primary" data-testid="chat-send-btn">
            {sending ? "…" : t("app.chat.send")}
          </button>
        </div>
        {voice.recording && (
          <p className="label-cap text-[var(--oxblood)] mt-3">● {t("app.voiceRecording")}</p>
        )}
        {voiceProcessing && (
          <p className="label-cap text-[var(--ink-soft)] mt-3">{t("app.voiceProcessing")}</p>
        )}
      </form>
    </div>
  );
}
